import { ExclamationCircleIcon } from '@heroicons/react/solid';
import { Autocomplete, Button, ButtonVariantEnum, Checkbox, TextInput } from '@Wonder-Cave/ui';
import { ISearchTcrCampaignsRecord } from '@shared/models';
import { FormikErrors, FormikTouched } from 'formik';
import { IClientLocalSystemNumberRecord } from '../../../../hooks/useClientLocalSystemNumbers';
import { IDropdownValue } from '../../../shared/Form/Dropdown';
import { tcrCampaignIdsForProviders, tcrCampaignLabel } from './number-management.helpers';
import { IPurchaseForm, IPurchaseReviewSummary } from './PurchaseNumbersModal';

interface IPurchaseNumbersFormProps {
  values: IPurchaseForm;
  errors: FormikErrors<IPurchaseForm>;
  touched: FormikTouched<IPurchaseForm>;
  isValid: boolean;
  isSearching: boolean;
  isConfirming: boolean;
  allProviderNames: string[];
  allTcrCampaigns: ISearchTcrCampaignsRecord[];
  tcrCampaignsLoading: boolean;
  records: IClientLocalSystemNumberRecord[];
  searchResult: IPurchaseReviewSummary | null;
  searchErrors: string[];
  onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  onReview: (summary: IPurchaseReviewSummary) => void;
  onConfirm: (summary: IPurchaseReviewSummary) => void;
  setFieldValue: <K extends keyof IPurchaseForm>(field: K, value: IPurchaseForm[K]) => void;
  setFieldTouched: (field: string) => void;
  onClearSearch: () => void;
}

const PurchaseNumbersForm = ({
  values,
  errors,
  touched,
  isValid,
  isSearching,
  isConfirming,
  allProviderNames,
  allTcrCampaigns,
  tcrCampaignsLoading,
  records,
  searchResult,
  searchErrors,
  onSubmit,
  onReview,
  onConfirm,
  setFieldValue,
  setFieldTouched,
  onClearSearch,
}: IPurchaseNumbersFormProps) => {
  const providerSet = new Set(values.selectedProviderNames);

  // tcrCampaignId is unique per client in production, so it is safe to use as the option value.
  const availableTcrCampaignIds = allTcrCampaigns.length > 0
    ? (values.selectedProviderNames.length === 0
      ? allTcrCampaigns.map(c => c.tcrCampaignId)
      : allTcrCampaigns.filter(c => c.provider?.name && providerSet.has(c.provider.name)).map(c => c.tcrCampaignId))
    : tcrCampaignIdsForProviders(records, values.selectedProviderNames);

  const campaignLabel = (tcrCampaignId: string): string => {
    const campaign = allTcrCampaigns.find(c => c.tcrCampaignId === tcrCampaignId);
    if (campaign) {
      const parts: string[] = [tcrCampaignId];
      if (campaign.provider?.name) parts.push(campaign.provider.name);
      if (campaign.messageType) parts.push(campaign.messageType);
      return parts.join(' - ');
    }
    return tcrCampaignLabel(tcrCampaignId, records, values.selectedProviderNames);
  };

  const providerOptions: IDropdownValue[] = allProviderNames.map(name => ({ label: name, value: name }));
  const tcrCampaignOptions: IDropdownValue[] = availableTcrCampaignIds.map(id => ({
    label: campaignLabel(id),
    value: id,
  }));

  const selectedProviderDropdownValues: IDropdownValue[] = values.selectedProviderNames.map(name => ({ label: name, value: name }));
  const selectedTcrDropdownValues: IDropdownValue[] = values.tcrCampaignIds.map(id => ({
    label: campaignLabel(id),
    value: id,
  }));

  const allProvidersSelected = allProviderNames.length > 0 && values.selectedProviderNames.length === allProviderNames.length;
  const allTcrSelected = availableTcrCampaignIds.length > 0 && values.tcrCampaignIds.length === availableTcrCampaignIds.length;

  const pruneTcrForProviders = (nextProviders: string[]) => {
    const nextProviderSet = new Set(nextProviders);
    const validIds = new Set(
      allTcrCampaigns.length > 0
        ? (nextProviders.length === 0
          ? allTcrCampaigns.map(c => c.tcrCampaignId)
          : allTcrCampaigns.filter(c => c.provider?.name && nextProviderSet.has(c.provider.name)).map(c => c.tcrCampaignId))
        : tcrCampaignIdsForProviders(records, nextProviders),
    );
    const pruned = values.tcrCampaignIds.filter(id => validIds.has(id));
    if (pruned.length !== values.tcrCampaignIds.length) {
      setFieldValue('tcrCampaignIds', pruned);
    }
  };

  const toggleSelectAllProviders = () => {
    setFieldTouched('selectedProviderNames');
    const next = allProvidersSelected ? [] : allProviderNames;
    setFieldValue('selectedProviderNames', next);
    pruneTcrForProviders(next);
    onClearSearch();
  };

  const toggleSelectAllTcr = () => {
    setFieldTouched('tcrCampaignIds');
    setFieldValue('tcrCampaignIds', allTcrSelected ? [] : availableTcrCampaignIds);
    onClearSearch();
  };

  const totalAvailable = searchResult
    ? searchResult.results.reduce((sum, r) => sum + r.phoneNumbers.length, 0)
    : null;
  const hasErrors = searchErrors.length > 0;

  return (
    <form onSubmit={onSubmit} className='flex flex-col w-full'>
      <div className='flex flex-col pb-6'>
        <div className='flex flex-row items-center justify-between pb-2'>
          <h5 className='text-medium-gray'>PROVIDER</h5>
          <Checkbox
            id='select-all-providers'
            name='select-all-providers'
            label='Select All'
            checked={allProvidersSelected}
            disabled={tcrCampaignsLoading || allProviderNames.length === 0}
            onChange={toggleSelectAllProviders}
          />
        </div>
        <Autocomplete
          label=''
          placeholder={tcrCampaignsLoading ? 'Loading providers...' : allProviderNames.length === 0 ? 'No providers available' : 'Type to search providers'}
          value={selectedProviderDropdownValues}
          options={providerOptions}
          multiple
          disabled={tcrCampaignsLoading}
          showError={!!(touched.selectedProviderNames && errors.selectedProviderNames)}
          error={touched.selectedProviderNames ? (errors.selectedProviderNames as string) : ''}
          onChange={(newValue?: IDropdownValue[]) => {
            setFieldTouched('selectedProviderNames');
            // !!v filters out undefined and empty strings; "v is string" narrows the type
            const next = (newValue ?? []).map(o => o?.value).filter((v): v is string => !!v);
            setFieldValue('selectedProviderNames', next);
            pruneTcrForProviders(next);
            onClearSearch();
          }}
          onBlur={() => setFieldTouched('selectedProviderNames')}
        />
      </div>

      <div className='flex flex-col pb-6'>
        <div className='flex flex-row items-center justify-between pb-2'>
          <h5 className='text-medium-gray'>SELECT TCR CAMPAIGNS</h5>
          <Checkbox
            id='select-all-tcr'
            name='select-all-tcr'
            label='Select All'
            checked={allTcrSelected}
            disabled={tcrCampaignsLoading || availableTcrCampaignIds.length === 0}
            onChange={toggleSelectAllTcr}
          />
        </div>
        <Autocomplete
          label=''
          placeholder={tcrCampaignsLoading ? 'Loading campaigns...' : availableTcrCampaignIds.length === 0 ? 'No campaigns available' : 'Type to search TCR campaigns'}
          value={selectedTcrDropdownValues}
          options={tcrCampaignOptions}
          multiple
          disabled={tcrCampaignsLoading}
          showError={!!(touched.tcrCampaignIds && errors.tcrCampaignIds)}
          error={touched.tcrCampaignIds ? (errors.tcrCampaignIds as string) : ''}
          onChange={(newValue?: IDropdownValue[]) => {
            setFieldTouched('tcrCampaignIds');
            setFieldValue('tcrCampaignIds', (newValue ?? []).map(o => o?.value).filter((v): v is string => !!v));
            onClearSearch();
          }}
          onBlur={() => setFieldTouched('tcrCampaignIds')}
        />
      </div>

      <div className='grid grid-cols-2 gap-x-8 pb-6'>
        <div className='flex flex-col'>
          <h5 className='pb-2 text-medium-gray'>AREA CODE</h5>
          <TextInput
            id='areaCode'
            name='areaCode'
            type='text'
            placeholder='Search area codes'
            value={values.areaCode}
            error={touched.areaCode ? errors.areaCode : ''}
            onBlur={() => setFieldTouched('areaCode')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldTouched('areaCode');
              setFieldValue('areaCode', event.target.value);
              onClearSearch();
            }}
          />
        </div>

        <div className='flex flex-col'>
          <h5 className='pb-2 text-medium-gray'>QUANTITY</h5>
          <TextInput
            id='quantity'
            name='quantity'
            type='number'
            placeholder='Quantity'
            value={values.quantity as any}
            error={touched.quantity ? errors.quantity : ''}
            onBlur={() => setFieldTouched('quantity')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldTouched('quantity');
              const parsed = Number(event.target.value);
              setFieldValue('quantity', Number.isFinite(parsed) ? parsed : 0);
              onClearSearch();
            }}
          />
        </div>
      </div>

      <div className='flex flex-row items-center pb-4 gap-8'>
        <Button
          variant={ButtonVariantEnum.PRIMARY}
          type='submit'
          disabled={!isValid || isSearching}
          isLoading={isSearching}
        >
          SEARCH
        </Button>

        {totalAvailable !== null && (
          <button
            type='button'
            className={`text-base font-normal underline cursor-pointer ${hasErrors ? 'text-flamingo' : 'text-wc-blue'}`}
            onClick={() => searchResult && onReview(searchResult)}
          >
            {totalAvailable} {totalAvailable === 1 ? 'Number' : 'Numbers'} Selected
          </button>
        )}

        <Button
          className='ml-auto'
          variant={ButtonVariantEnum.TERTIARY}
          type='button'
          disabled={totalAvailable === null || totalAvailable === 0 || isSearching || isConfirming}
          isLoading={isConfirming}
          onClick={() => searchResult && onConfirm(searchResult)}
        >
          PURCHASE
        </Button>
      </div>

      {searchErrors.length > 0 && (
        <div className='flex items-center gap-2 pb-4 text-small font-bold text-black'>
          <ExclamationCircleIcon className='w-3 h-3 text-flamingo flex-shrink-0' />
          One or more issues found with your number purchase.
        </div>
      )}
    </form>
  );
};

export default PurchaseNumbersForm;
